/**
 * Object-scale helpers — world-space bounding-box diagonal for a scene
 * object or an edit-mode selection.
 *
 * Used by sliders whose world-space ranges should scale with the target's
 * size (bevel offset, inset thickness, solidify depth, etc.) so a 5 cm
 * screw head and a 5 m archway both get a useful slider span instead of a
 * fixed world cap that's appropriate for neither.
 *
 * All readers fall back to `FALLBACK_DIAGONAL` when the object has no
 * geometry or when the selection is empty — callers don't have to guard
 * for degenerate inputs.
 */

import type { CurveData, EditModeState, SceneObject } from "./types.ts";

/**
 * Diagonal used when the target has no usable geometry (empty mesh, no
 * selection, scene object not found). One meter matches a default-scale
 * cube and produces sensible slider ranges in the void.
 */
export const FALLBACK_DIAGONAL = 1;

function diagonalFromExtents(
  minX: number,
  maxX: number,
  minY: number,
  maxY: number,
  minZ: number,
  maxZ: number,
): number {
  const dx = maxX - minX;
  const dy = maxY - minY;
  const dz = maxZ - minZ;
  const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
  return d > 0 ? d : FALLBACK_DIAGONAL;
}

/** Bounding-box diagonal of every vertex in the flat (x,y,z) buffer. */
export function verticesDiagonal(vertices: Float32Array): number {
  if (vertices.length < 3) return FALLBACK_DIAGONAL;
  let minX = Infinity,
    minY = Infinity,
    minZ = Infinity;
  let maxX = -Infinity,
    maxY = -Infinity,
    maxZ = -Infinity;
  for (let i = 0; i < vertices.length; i += 3) {
    const x = vertices[i];
    const y = vertices[i + 1];
    const z = vertices[i + 2];
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
  }
  return diagonalFromExtents(minX, maxX, minY, maxY, minZ, maxZ);
}

/** Bounding-box diagonal of a subset of vertex indices. */
export function selectedVerticesDiagonal(
  vertices: Float32Array,
  indices: Iterable<number>,
): number {
  let minX = Infinity,
    minY = Infinity,
    minZ = Infinity;
  let maxX = -Infinity,
    maxY = -Infinity,
    maxZ = -Infinity;
  let count = 0;
  for (const vi of indices) {
    const x = vertices[vi * 3];
    const y = vertices[vi * 3 + 1];
    const z = vertices[vi * 3 + 2];
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
    count++;
  }
  if (count === 0) return FALLBACK_DIAGONAL;
  return diagonalFromExtents(minX, maxX, minY, maxY, minZ, maxZ);
}

/**
 * Apply a scene object's non-uniform scale to a local-space diagonal.
 * World-space diagonals drive UI ranges so a 2×-scaled object's bevel
 * slider ought to span twice as far as its local counterpart.
 *
 * Uses the L2 norm of the scaled bounding-box edge lengths, which is
 * exact when the object is axis-aligned in world space and a close
 * approximation otherwise — rotations don't change the diagonal in
 * world units appreciably for UI purposes.
 */
export function applyObjectScale(
  localDiagonal: number,
  scale: [number, number, number],
): number {
  // localDiagonal = sqrt(dx² + dy² + dz²). Without access to the
  // individual extents the best we can do is a uniform approximation:
  // geometric mean of the three scale factors. This is close for
  // near-cubic shapes and conservative for long thin objects.
  const geom = Math.cbrt(
    Math.abs(scale[0]) * Math.abs(scale[1]) * Math.abs(scale[2]),
  );
  return localDiagonal * (geom > 0 ? geom : 1);
}

/**
 * World-space bounding-box diagonal of a scene object — vertices in
 * object-local space multiplied by the object's world scale.
 *
 * Rotation/position don't change the diagonal, so this is effectively
 * the object's size in meters.
 */
export function objectDiagonal(obj: SceneObject | undefined): number {
  if (!obj) return FALLBACK_DIAGONAL;
  const local = verticesDiagonal(obj.vertices);
  return applyObjectScale(local, obj.scale);
}

/**
 * Diagonal of the current edit-mode selection in world units, falling
 * back to the full-object diagonal when no elements are selected.
 *
 * In face/edge granularity we derive the vertex set from the active
 * selection; in vertex mode we use `selectedVertices` directly. When the
 * result is empty we return the whole-object diagonal — the user's
 * intent for "scale the slider" when no selection exists is best served
 * by the outer mesh.
 */
export function editSelectionDiagonal(
  obj: SceneObject,
  em: EditModeState,
): number {
  const indices =
    em.granularity === "vertex"
      ? em.selectedVertices
      : collectSelectedVertices(obj, em);
  const local =
    indices.size > 0
      ? selectedVerticesDiagonal(obj.vertices, indices)
      : verticesDiagonal(obj.vertices);
  return applyObjectScale(local, obj.scale);
}

/**
 * World-space bounding-box diagonal of a curve's control points.
 *
 * Curves bake a tube whose output bbox is inflated by the current bevel
 * depth, which makes `objectDiagonal` (which reads SceneObject.vertices)
 * feed back into any slider that drives depth. Control-point extent is
 * stable — it reflects the path the user authored, independent of bevel
 * state — and so produces a non-oscillating reference size for the
 * bevel depth slider.
 */
export function curveControlDiagonal(
  curve: CurveData,
  objectScale: [number, number, number],
): number {
  if (curve.points.length === 0) return FALLBACK_DIAGONAL;
  let minX = Infinity,
    minY = Infinity,
    minZ = Infinity;
  let maxX = -Infinity,
    maxY = -Infinity,
    maxZ = -Infinity;
  for (const p of curve.points) {
    const [x, y, z] = p.co;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
  }
  const local = diagonalFromExtents(minX, maxX, minY, maxY, minZ, maxZ);
  return applyObjectScale(local, objectScale);
}

function collectSelectedVertices(
  obj: SceneObject,
  em: EditModeState,
): Set<number> {
  const set = new Set<number>();
  const { offsets, corners, edges } = obj.topology;
  if (em.granularity === "face") {
    for (const f of em.selectedFaces) {
      const start = offsets[f];
      const end = offsets[f + 1];
      for (let j = start; j < end; j++) set.add(corners[j]);
    }
  } else if (em.granularity === "edge") {
    for (const ei of em.selectedEdges) {
      set.add(edges[ei * 2]);
      set.add(edges[ei * 2 + 1]);
    }
  }
  return set;
}
